import { type ReactNode } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "noma.to";
const DEFAULT_DESCRIPTION =
  "Discover curated outdoor adventures off the beaten path. Hidden gems and local favorites from explorers who know.";
const DEFAULT_OG_IMAGE = "https://noma.to/og-default.jpg";

interface PageHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  image?: string;
  noIndex?: boolean;
  author?: string;
  publishedAt?: Date | null;
  updatedAt?: Date | null;
  jsonLd?: object;
  children?: ReactNode;
}

export function PageHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  image,
  noIndex = false,
  author,
  publishedAt,
  updatedAt,
  jsonLd,
  children,
}: PageHeadProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Off the beaten path`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {author && <meta name="author" content={author} />}

      {/* Open Graph */}
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={image || DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content={SITE_NAME} />
      {publishedAt && <meta property="article:published_time" content={publishedAt.toISOString()} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt.toISOString()} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || DEFAULT_OG_IMAGE} />

      {/* JSON-LD Structured Data */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}

      {children}
    </Helmet>
  );
}
